'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { ClassicPdpBreadcrumb } from '../classic-pdp-content';

export interface ClassicPdpBreadcrumbsProps {
  crumbs: ClassicPdpBreadcrumb[];
}

/**
 * Breadcrumb trail above the two-column grid. Every crumb but the last one
 * links its page; the last one names the current page and therefore carries no
 * link.
 */
export function ClassicPdpBreadcrumbs({ crumbs }: Readonly<ClassicPdpBreadcrumbsProps>) {
  const t = useTranslations('ClassicProductDetail');
  const { localizedLink } = useLocaleLink();

  if (crumbs.length === 0) {
    return null;
  }

  return (
    <nav aria-label={t('breadcrumb')} data-testid="classic-pdp-breadcrumbs" className="mb-6">
      <ol className="flex flex-wrap items-center gap-2 text-sm text-neutral-55">
        {crumbs.map((crumb, index) => (
          <li key={`${crumb.path ?? 'current'}-${crumb.label}`} className="flex items-center gap-2">
            {index > 0 && <span aria-hidden="true">/</span>}
            {crumb.path ? (
              <Link href={localizedLink(crumb.path)} className="hover:underline">
                {crumb.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-neutral-100">
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
